/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
/** Copied file from portal implementation as is */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { flushSync } from 'react-dom';
import { compose, map, splitEvery, flatten, clamp } from 'rambda';
import { noop, pipe } from 'utils/fp';
import { AnyObject } from 'types';
import { getInvertedTransform } from 'utils/konva/drawingHelpers';
import { MEDIA_GROUP_ID } from 'utils/konva/types';
import { StageContext } from 'modules/Canvas/contexts/StageContext';

import {
  makeGetRestrictedPosition,
  getDimensions,
  makeCirclesFromPoints,
  getRelativePointerPosition,
  getNearestMidPoint,
  makePolygonDimensions,
  getLineRect,
} from 'modules/Canvas/components/Canvas/helpers/drawingHelpers';

/**
 * Hook dragging, point grabbing, and adding new points.
 */
export const usePolyEditing = ({
  points,
  container,
  refs: { shapeRef },
  onComplete = noop,
  isSelected,
  mouseEvent,
  enableAddPoints,
}: {
  points: [number, number][];
} & AnyObject) => {
  const containerDimensions = getDimensions(container);
  const [shapeData, setShapeData] = useState({ points });
  const [polyData, setPolyData] = useState(false);
  const { stageRef } = useContext(StageContext);

  const dimensions = makePolygonDimensions({ points });
  const scale = shapeRef.current ? shapeRef.current.getLayer().children[0].scaleX() : 1;
  const parseFunction = useCallback(
    makeGetRestrictedPosition({
      containerDimensions,
      dimensions,
      scale,
    }),
    [container, shapeData],
  );
  const [tempPoint, setTempPoint] = useState(null);
  const [grabbedHandle, setGrabbedHandle] = useState(null);
  const isGrabbingHandle = grabbedHandle !== null;

  const mediaGroup = stageRef.current?.find(`#${MEDIA_GROUP_ID}`)?.[0];

  const mousePosition = (() => {
    const { x, y } = mediaGroup
      ? getRelativePointerPosition(
          mouseEvent || {
            target: { getStage: () => stageRef.current },
          },
          getInvertedTransform(mediaGroup),
        )
      : { x: 0, y: 0 };

    return mouseEvent?.target
      ? {
          x: clamp(0, containerDimensions.width, x),
          y: clamp(0, containerDimensions.height, y),
        }
      : {
          x: 0,
          y: 0,
        };
  })();

  useEffect(() => {
    const posAbs = shapeRef.current && shapeRef.current.getAbsolutePosition();
    const rect = shapeRef.current && shapeRef.current.getClientRect();
    setPolyData({
      posAbs,
      rect,
    });
  }, [shapeData]);

  useEffect(() => {
    if (!polyData && shapeRef.current) {
      const posAbs = shapeRef.current && shapeRef.current.getAbsolutePosition();
      const rect = shapeRef.current && shapeRef.current.getClientRect();
      setPolyData({
        posAbs,
        rect,
      });
    }
  });

  const onDragEnd = ({ currentTarget }) => {
    const attrs = {
      x: 0,
      y: 0,
      points: pipe(
        currentTarget.points(),
        splitEvery(2),
        map(([x, y]) => [x + currentTarget.x(), y + currentTarget.y()]),
      ),
    };

    currentTarget.setAttrs(flatten(attrs));
    setShapeData((prevProps) => ({ ...prevProps, ...attrs }));
    onComplete(attrs);
  };

  const onDragMove = useCallback(
    ({ currentTarget }) => {
      const { posAbs, rect } = polyData;
      const allowedCoords = compose(parseFunction, () => getLineRect(currentTarget, container))();
      const deltaY = rect.y - posAbs.y;
      const deltaX = rect.x - posAbs.x;
      const coordsWithDelta = {
        x: allowedCoords.x - deltaX,
        y: allowedCoords.y - deltaY,
      };
      const newPos = {
        x: coordsWithDelta.x / scale,
        y: coordsWithDelta.y / scale,
      };

      currentTarget.position(newPos);
      setShapeData((prevProps) => ({ ...prevProps, ...newPos }));
    },
    [shapeData, setShapeData, polyData],
  );

  useEffect(() => {
    shapeRef.current.getLayer().batchDraw();
  }, [shapeData]);

  // allow new points by showing midpoint circles on edge when mouse gets close
  useEffect(() => {
    // if grabbing, do NOT try to set the temp point based on proximity to nearest edge
    if (enableAddPoints && isSelected && mousePosition && !isGrabbingHandle) {
      const { x, y } = mousePosition;
      const nearest = getNearestMidPoint(shapeData.points, { x, y });
      setTempPoint(nearest);
    }
    return () => {
      if (!isSelected) setTempPoint(null);
    };
  }, [isSelected, mousePosition, isGrabbingHandle, shapeData.points]);

  // points with the temp mid point inserted
  let editablePoints = useMemo(() => {
    if (tempPoint) {
      const newPoints = [...shapeData.points];

      newPoints.splice(tempPoint.pointIndex, 0, tempPoint.point);
      return newPoints;
    }
    return shapeData.points;
  }, [shapeData.points, tempPoint, grabbedHandle]);

  // when grabbing, have the grabbed handle track the mouse position
  editablePoints = useMemo(() => {
    if (grabbedHandle && mousePosition) {
      const editablePointsWithGrabbed = [...editablePoints];
      editablePointsWithGrabbed.splice(grabbedHandle.index, 1, [mousePosition.x, mousePosition.y]);
      return editablePointsWithGrabbed;
    }
    return editablePoints;
  }, [editablePoints, grabbedHandle, mousePosition]);

  // circles on the points
  const handles = useMemo(() => {
    if (isSelected) {
      // show handles
      return makeCirclesFromPoints(editablePoints, scale);
    }
    return [];
  }, [editablePoints, isSelected]);

  const handleGrabStart = (e) => {
    const { id, x, y } = e.currentTarget.attrs;
    const index = Number(id);
    const tempPointIsGrabbed = index === tempPoint?.pointIndex;

    // temp point was not grabbed, remove it
    if (tempPoint && !tempPointIsGrabbed) {
      // adjust index for removed tempPoint (if necessary)
      const adjustedIndex = tempPoint.pointIndex < index ? index - 1 : index;

      flushSync(() => {
        setGrabbedHandle({
          index: adjustedIndex,
          point: [x, y],
        });
        setTempPoint(null);
      });
    } else {
      setGrabbedHandle({
        index,
        point: [x, y],
      });
    }
  };

  const handleGrabEnd = () => {
    const attrs = { points: editablePoints };

    setShapeData((prevProps) => ({ ...prevProps, ...attrs }));
    onComplete(attrs);
    setTempPoint(null);
    setGrabbedHandle(null);
  };

  return {
    shapeProps: {
      ...shapeData,
      points: editablePoints,
      handles,
    },
    polygonEventHandlers: {
      onDragMove,
      onDragEnd,
    },
    handleEventHandlers: {
      handleGrabStart,
      handleGrabEnd,
    },
  };
};
